import { createContext, useContext } from 'react';
import { BarChart3, BookOpen, Inbox, Users, type LucideIcon } from 'lucide-react';

export type AdminSectionPage = { to: string; labelKey: string; testId: string };
export type AdminSection = {
  testId: string;
  labelKey: string;
  icon: LucideIcon;
  /** The first page is where the sidebar link goes. */
  pages: AdminSectionPage[];
};

/**
 * The supervisor's four categories (owner, 2026-10-20, #12) — the same
 * grouping as the phone (src/navigation/adminSections.ts). The routes stay
 * /admin/<page>; the sidebar shows the four, and each page's header becomes
 * its category's hero with the category's pages as pills inside it.
 */
export const ADMIN_SECTIONS: AdminSection[] = [
  {
    testId: 'nav-admin-requests', labelKey: 'admin.section.requests', icon: Inbox,
    pages: [{ to: '/admin/requests', labelKey: 'admin.tab.requests', testId: 'section-requests' }],
  },
  {
    testId: 'nav-admin-people', labelKey: 'admin.section.people', icon: Users,
    pages: [
      { to: '/admin/users', labelKey: 'admin.tab.users', testId: 'section-users' },
      { to: '/admin/schools', labelKey: 'schools.nav', testId: 'section-schools' },
    ],
  },
  {
    testId: 'nav-admin-content', labelKey: 'admin.section.content', icon: BookOpen,
    pages: [
      { to: '/admin/curricula', labelKey: 'admin.tab.curricula', testId: 'section-curricula' },
      { to: '/admin/tracks', labelKey: 'admin.tab.tracks', testId: 'section-tracks' },
      { to: '/admin/game', labelKey: 'game.admin.nav', testId: 'section-game' },
    ],
  },
  {
    testId: 'nav-admin-monitor', labelKey: 'admin.section.monitor', icon: BarChart3,
    pages: [
      { to: '/admin/sessions', labelKey: 'admin.tab.sessions', testId: 'section-sessions' },
      { to: '/admin/stats', labelKey: 'admin.tab.stats', testId: 'section-stats' },
    ],
  },
];

/** The category a path belongs to, or null outside the supervisor portal. */
export function sectionForPath(pathname: string): AdminSection | null {
  return ADMIN_SECTIONS.find((s) => s.pages.some((p) => pathname === p.to || pathname.startsWith(`${p.to}/`))) ?? null;
}

/** Set by the shell on a supervisor page; PageHeader draws the hero from it. */
export const AdminSectionContext = createContext<AdminSection | null>(null);
export const useAdminSection = () => useContext(AdminSectionContext);
